import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { v4 as uuid } from 'uuid';

import { CHEVRON_RIGHT_ICON, CHEVRON_LEFT_ICON } from '../../../constants/images';
import { formatDateYYYYMMDDTT } from '../../../utils/datetime/datetime';
import { capitalize } from '../../../utils/utils';
import BdsIconButton from '../Buttons/BdsIconButton/BdsIconButton';

import './StyledCalendar.scss';

type DateRange = {
  start: string | null;
  end: string | null;
};

interface IStyledCalendarProps {
  title?: string;
  selectedRange: DateRange;
  locale?: string;
  setSelectedRange: (dateRange: DateRange) => void;
}

const StyledCalendar = ({ title, selectedRange, locale = 'en', setSelectedRange }: IStyledCalendarProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [internalRange, setInternalRange] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  });
  const thisYear = new Date().getFullYear();

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setInternalRange({
      start: today,
      end: selectedRange.end ? new Date(selectedRange.end) : null
    });
  }, [selectedRange]);

  const weekdays = useMemo(() => {
    const weekdayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    return Array.from(Array(7)).map((_, i) => {
      const date = new Date(2024, 0, i + 1);
      return weekdayFormatter.format(date);
    });
  }, [locale]);

  const months = useMemo(() => {
    const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'long' });
    return Array.from(Array(12)).map((_, i) => {
      const date = new Date(thisYear, i, 1);
      return monthFormatter.format(date);
    });
  }, [thisYear, locale]);

  const getMonthData = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

    const createDateForCell = (cellIndex: number) => {
      if (cellIndex < startingDay) {
        return new Date(year, month, cellIndex - startingDay + 1);
      }
      const dayOfMonth = cellIndex - startingDay + 1;
      if (dayOfMonth > daysInMonth) {
        return new Date(year, month + 1, dayOfMonth - daysInMonth);
      }
      return new Date(year, month, dayOfMonth);
    };

    const monthData = [];
    let week = [];
    for (let i = 0; i < 42; i++) {
      week.push(createDateForCell(i));
      if (week.length === 7) {
        monthData.push(week);
        week = [];
        if (monthData.length === 5 && i >= startingDay + daysInMonth - 1) {
          break;
        }
      }
    }

    return monthData;
  }, []);

  const isPastDate = useCallback((date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  }, []);

  const isDateDisabled = useCallback(
    (date: Date) => {
      if (isPastDate(date)) return true;
      if (internalRange.start && !internalRange.end) {
        return date < internalRange.start;
      }
      return false;
    },
    [internalRange, isPastDate]
  );

  const isDateGhost = (date: Date, index: number) => {
    const dateDay = date.getDate();
    if (dateDay > index + 1 || dateDay * 6 < index) return true;
    return false;
  };

  const handleDateClick = useCallback(
    (date: Date) => {
      if (isDateDisabled(date)) return;

      if (!internalRange.start || (internalRange.start && internalRange.end)) {
        setInternalRange({ start: currentDate, end: null });
        setSelectedRange({ start: formatDateYYYYMMDDTT(currentDate), end: null });
      } else if (date < internalRange.start) {
        setInternalRange({ start: date, end: null });
        setSelectedRange({ start: formatDateYYYYMMDDTT(date), end: null });
      } else {
        setInternalRange({ ...internalRange, end: date });
        setSelectedRange({ start: formatDateYYYYMMDDTT(internalRange.start), end: formatDateYYYYMMDDTT(date, true) });
      }
    },
    [internalRange, currentDate]
  );

  const isDateInRange = useCallback(
    (date: Date) => {
      if (!internalRange.start || !internalRange.end) return false;
      return date >= internalRange.start && date <= internalRange.end;
    },
    [internalRange]
  );

  const isDateInHoverRange = useCallback(
    (date: Date) => {
      if (!internalRange.start || internalRange.end || !hoverDate) return false;
      return (date > internalRange.start && date <= hoverDate) || (date < internalRange.start && date >= hoverDate);
    },
    [internalRange, hoverDate]
  );

  const getCurveDown = (month: Date, date: Date, index: number) => {
    const startDateDate = internalRange.start?.getDate();
    const datesMonth = date.getMonth();
    const currentMonth = month.getMonth();

    const currentMonthIsDatesMonth = currentMonth === datesMonth;
    const weekIsPair = Math.floor(index + 1 / 7) % 2 === 0;
    const curveDown =
      startDateDate != null
        ? (!currentMonthIsDatesMonth && weekIsPair) ||
          (currentMonthIsDatesMonth && Math.floor((startDateDate - index + 1) / 7) % 2 === 0)
        : '';
    if (curveDown) return 'down';
    else return 'up';
  };

  const dateClassName = useCallback(
    (month: Date, date: Date, index: number) => {
      const stringDate = date.toDateString();
      const datesMonth = date.getMonth();
      const currentMonth = month.getMonth();

      return `
      calendar-day has-date
      ${isDateInRange(date) ? 'in-range' : ''}
      ${stringDate === internalRange.start?.toDateString() && currentMonth === datesMonth ? 'range-start' : ''}
      ${stringDate === internalRange.end?.toDateString() && currentMonth === datesMonth ? 'range-end' : ''}
      ${internalRange.start && internalRange.end && currentMonth === datesMonth ? 'range-selected' : ''}
      ${index % 7 === 0 ? `day-start ${getCurveDown(month, date, index)}` : ''}
      ${(index + 1) % 7 === 0 ? `day-end ${getCurveDown(month, date, index)}` : ''}
      ${isDateInHoverRange(date) && currentMonth === datesMonth ? 'hover-range' : ''}
      ${isDateDisabled(date) ? 'disabled' : ''}
      ${isDateGhost(date, index) ? 'ghost' : ''}
    `;
    },
    [internalRange.end, internalRange.start, isDateDisabled, isDateInHoverRange, isDateInRange]
  );

  const renderMonth = useCallback(
    (monthOffset: number) => {
      const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + monthOffset, 1);
      const monthData = getMonthData(monthDate);

      return (
        <div className="month">
          <h2 className="month-title">
            {capitalize(months[monthDate.getMonth()])} {monthDate.getFullYear()}
          </h2>
          <div className="calendar-grid">
            {weekdays.map((day) => (
              <div key={day} className="day-name">
                {day}
              </div>
            ))}
            {monthData.flat().map((date, index) => (
              <button
                key={uuid()}
                className={date ? dateClassName(monthDate, date, index) : 'calendar-day'}
                onClick={() => date && !isDateDisabled(date) && handleDateClick(date)}
                onMouseEnter={() => date && setHoverDate(date)}
                onMouseLeave={() => setHoverDate(null)}
              >
                {date ? date.getDate() : ''}
              </button>
            ))}
          </div>
        </div>
      );
    },
    [currentDate, getMonthData, months, weekdays, dateClassName, isDateDisabled, handleDateClick]
  );

  const handlePrevMonth = useCallback(() => {
    setCurrentDate((prevDate) => new Date(prevDate.getFullYear(), prevDate.getMonth() - 1, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentDate((prevDate) => new Date(prevDate.getFullYear(), prevDate.getMonth() + 1, 1));
  }, []);

  return (
    <div className="styled-calendar">
      {title && <p className="calendar-title">{title}</p>}
      <div className="calendar-content">
        <div className="calendar-navigation">
          <BdsIconButton icon={CHEVRON_LEFT_ICON} action={handlePrevMonth} size="md" variant="transparent" />
          <BdsIconButton icon={CHEVRON_RIGHT_ICON} action={handleNextMonth} size="md" variant="transparent" />
        </div>
        <div className="months-container">
          {renderMonth(0)}
          {renderMonth(1)}
          {renderMonth(2)}
        </div>
      </div>
    </div>
  );
};

export default React.memo(StyledCalendar);
